
import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Info } from "lucide-react";
import { type NetworkRule } from "@/hooks/useNetworkRules";
import { useUpdateBundleId } from "@/hooks/useNetworkRules";
import { useToast } from "@/hooks/use-toast";

interface SecondaryBundleIdSectionProps {
  rule: NetworkRule;
  isEditing?: boolean;
}

export const SecondaryBundleIdSection = ({ rule, isEditing = true }: SecondaryBundleIdSectionProps) => {
  const [useSecondaryBundleId, setUseSecondaryBundleId] = useState(false);
  const [secondaryBundleId, setSecondaryBundleId] = useState("");
  const [originalBundleId, setOriginalBundleId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const updateBundleIdMutation = useUpdateBundleId();
  const { toast } = useToast();

  // Load saved state from localStorage and initialize
  useEffect(() => {
    const savedData = localStorage.getItem(`secondaryBundleId_${rule.id}`);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setUseSecondaryBundleId(parsed.useSecondaryBundleId || false);
        setSecondaryBundleId(parsed.secondaryBundleId || "");
        setOriginalBundleId(parsed.originalBundleId || rule.bundleId || "");
      } catch (e) {
        console.warn('Failed to parse saved secondary bundle ID data');
      }
    } else {
      // First time - store the original bundle ID
      setOriginalBundleId(rule.bundleId || "");
    }
  }, [rule.id, rule.bundleId]);

  const handleSave = async () => {
    if (useSecondaryBundleId && !secondaryBundleId.trim()) {
      toast({
        title: "Alternative Bundle ID Required",
        description: "Please enter an alternative bundle ID or disable the option.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Determine which bundle ID to use
      const targetBundleId = useSecondaryBundleId ? secondaryBundleId.trim() : originalBundleId;
      
      // Update the rule's bundle ID in the API
      await updateBundleIdMutation.mutateAsync({
        ruleId: rule.id,
        bundleId: targetBundleId
      });

      // Save the state to localStorage
      const dataToSave = {
        useSecondaryBundleId,
        secondaryBundleId: secondaryBundleId.trim(),
        originalBundleId: originalBundleId
      };
      localStorage.setItem(`secondaryBundleId_${rule.id}`, JSON.stringify(dataToSave));

      toast({
        title: "Bundle ID Updated",
        description: useSecondaryBundleId 
          ? `Rule now uses alternative bundle ID: ${secondaryBundleId.trim()}`
          : "Rule reverted to original bundle ID",
      });
    } catch (error) {
      console.error('Error updating bundle ID:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update the bundle ID. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = useSecondaryBundleId !== (rule.useSecondaryBundleId || false) || 
                    secondaryBundleId !== (rule.secondaryBundleId || "");

  if (!isEditing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Alternative Bundle ID</CardTitle>
        </CardHeader>
        <CardContent>
          {rule.useSecondaryBundleId ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium">Enabled</span>
              </div>
              <div className="text-sm text-gray-600">
                Active Bundle ID: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{rule.secondaryBundleId}</code>
              </div>
              <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800">
                  This rule is using the alternative bundle ID as its primary target, overriding the original bundle ID.
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span className="text-sm text-gray-600">Disabled - Using original bundle ID</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Alternative Bundle ID</CardTitle>
        <CardDescription>
          Override the main bundle ID with a different target bundle identifier
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Simplified approach notice */}
        <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-800">
            <strong>Simplified Approach:</strong> Alternative bundle ID functionality works by directly updating 
            the rule's bundle ID field. The original bundle ID is preserved locally for easy restoration.
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Switch
            checked={useSecondaryBundleId}
            onCheckedChange={setUseSecondaryBundleId}
            disabled={isLoading}
          />
          <Label className="text-sm font-medium">
            Use Alternative Bundle ID
          </Label>
        </div>

        {/* Show original bundle ID */}
        {originalBundleId && (
          <div className="space-y-2">
            <Label className="text-sm text-gray-600">Original Bundle ID</Label>
            <div className="text-sm font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded">
              {originalBundleId}
            </div>
          </div>
        )}

        {useSecondaryBundleId && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="secondary-bundle-id" className="text-sm">
                Alternative Bundle ID
              </Label>
              <Input
                id="secondary-bundle-id"
                value={secondaryBundleId}
                onChange={(e) => setSecondaryBundleId(e.target.value)}
                placeholder="e.g., com.example.actualbundle"
                disabled={isLoading}
              />
            </div>

            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800">
                <strong>Important:</strong> When enabled, this alternative bundle ID will replace the current bundle ID 
                as the primary target for this rule. You can always revert to the original bundle ID by disabling this option.
              </div>
            </div>
          </div>
        )}

        {hasChanges && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={isLoading || (useSecondaryBundleId && !secondaryBundleId.trim())}
              size="sm"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
